import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import FirebaseService from 'services/FirebaseService';
import AuthService from 'services/AuthService';

const getRoleFromToken = (token) => {
	try {
		const claims = JSON.parse(window.atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
		const authorities = claims.authorities || ''
		return authorities.includes('ROLE_ADMIN') ? 'ROLE_ADMIN' : 'ROLE_USER'
	} catch (e) {
		return null
	}
}

const storedToken = localStorage.getItem(AUTH_TOKEN)

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: '',
	token: storedToken || null,
	role: storedToken ? getRoleFromToken(storedToken) : null
}

export const signOut = createAsyncThunk('auth/signOut', async () => {
	await FirebaseService.signOutRequest()
	localStorage.removeItem(AUTH_TOKEN)
})

export const signIn = createAsyncThunk('auth/signIn', async (data, { rejectWithValue }) => {
	try {
		const response = await AuthService.login(data)
		const token = response.token
		localStorage.setItem(AUTH_TOKEN, token)
		return token
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || err.message || 'Invalid credentials')
	}
})

export const sendLoginOtp = createAsyncThunk('auth/sendLoginOtp', async (data, { rejectWithValue }) => {
	try {
		await AuthService.sendLoginOtp(data)
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || err.message || 'Error sending OTP')
	}
})

export const verifyLoginOtp = createAsyncThunk('auth/verifyLoginOtp', async (data, { rejectWithValue }) => {
	try {
		const response = await AuthService.verifyLoginOtp(data)
		const token = response.token
		localStorage.setItem(AUTH_TOKEN, token)
		return token
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || err.message || 'Invalid OTP code')
	}
})

export const sendOtp = createAsyncThunk('auth/sendOtp', async (data, { rejectWithValue }) => {
	try {
		await AuthService.sendOtp(data)
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || err.message || 'Error sending OTP')
	}
})

export const verifyOtp = createAsyncThunk('auth/verifyOtp', async (data, { rejectWithValue }) => {
	try {
		const response = await AuthService.verifyOtp(data)
		return response.temporaryToken
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || err.message || 'Invalid OTP code')
	}
})

export const register = createAsyncThunk('auth/register', async (data, { rejectWithValue }) => {
	try {
		await AuthService.register(data)
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || err.message || 'Registration failed')
	}
})

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		authenticated: (state, action) => {
			state.loading = false
			state.redirect = '/'
			state.token = action.payload
			state.role = getRoleFromToken(action.payload)
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload
			state.showMessage = true
			state.loading = false
		},
		hideAuthMessage: (state) => {
			state.message = ''
			state.showMessage = false
		},
		signOutSuccess: (state) => {
			state.loading = false
			state.token = null
			state.role = null
			state.redirect = '/'
		},
		showLoading: (state) => {
			state.loading = true
		},
		signInSuccess: (state, action) => {
			state.loading = false
			state.token = action.payload
			state.role = getRoleFromToken(action.payload)
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false
				state.token = null
				state.role = null
				state.redirect = '/'
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false
				state.token = null
				state.role = null
				state.redirect = '/'
			})
			.addCase(signIn.pending, (state) => {
			state.loading = true
		})
		.addCase(signIn.fulfilled, (state, action) => {
			state.loading = false
			state.redirect = '/'
			state.token = action.payload
			state.role = getRoleFromToken(action.payload)
		})
		.addCase(signIn.rejected, (state, action) => {
			state.loading = false
			state.message = action.payload
			state.showMessage = true
		})
		.addCase(sendLoginOtp.pending, (state) => {
				state.loading = true
			})
			.addCase(sendLoginOtp.fulfilled, (state) => {
				state.loading = false
			})
			.addCase(sendLoginOtp.rejected, (state, action) => {
				state.loading = false
				state.message = action.payload
				state.showMessage = true
			})
			.addCase(verifyLoginOtp.pending, (state) => {
				state.loading = true
			})
			.addCase(verifyLoginOtp.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
				state.role = getRoleFromToken(action.payload)
			})
			.addCase(verifyLoginOtp.rejected, (state, action) => {
				state.loading = false
				state.message = action.payload
				state.showMessage = true
			})
			.addCase(sendOtp.pending, (state) => {
				state.loading = true
			})
			.addCase(sendOtp.fulfilled, (state) => {
				state.loading = false
			})
			.addCase(sendOtp.rejected, (state, action) => {
				state.loading = false
				state.message = action.payload
				state.showMessage = true
			})
			.addCase(verifyOtp.pending, (state) => {
				state.loading = true
			})
			.addCase(verifyOtp.fulfilled, (state) => {
				state.loading = false
			})
			.addCase(verifyOtp.rejected, (state, action) => {
				state.loading = false
				state.message = action.payload
				state.showMessage = true
			})
			.addCase(register.pending, (state) => {
				state.loading = true
			})
			.addCase(register.fulfilled, (state) => {
				state.loading = false
			})
			.addCase(register.rejected, (state, action) => {
				state.loading = false
				state.message = action.payload
				state.showMessage = true
			})
	},
})

export const {
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess
} = authSlice.actions

export default authSlice.reducer
